import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BellRing, Sparkles, Bell } from 'lucide-react';
import axios from '../../axiosConfig';
import Badge from '../../components/ui/Badge';
import { ChangelogPanel } from './ChangelogDropdown';
import './HeaderOverflowMenu.css';

/**
 * Mobile-only overflow menu that consolidates Sparkles + Bell into one button.
 * Tapping the kebab opens a two-item popover (What's New / Notifications),
 * each with its own unread count. Tapping "What's New" reuses the same
 * ChangelogPanel the desktop dropdown renders; "Notifications" navigates.
 *
 * Hidden ≥769px by `.ha-mobile-only` — the inline icons take over there.
 */
const HeaderOverflowMenu = () => {
  const navigate = useNavigate();
  const notifications = useSelector((s) => s.notifications?.items || []);
  const notificationUnread = notifications.filter((n) => !n.isRead).length;

  const [menuOpen, setMenuOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [changelogUnread, setChangelogUnread] = useState(0);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const panelRef = useRef(null);

  const fetchChangelogUnread = useCallback(async () => {
    try {
      const res = await axios.get('/merchant/changelog/unread-count');
      setChangelogUnread(res.data?.unreadCount || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchChangelogUnread(); }, [fetchChangelogUnread]);

  // Outside-click closes whichever surface is currently open. Menu and panel
  // are mutually exclusive — opening the panel auto-closes the menu.
  useEffect(() => {
    if (!menuOpen && !changelogOpen) return undefined;
    const onClick = (ev) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      const p = panelRef.current;
      if (t && t.contains(ev.target)) return;
      if (m && m.contains(ev.target)) return;
      if (p && p.contains(ev.target)) return;
      setMenuOpen(false);
      setChangelogOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen, changelogOpen]);

  const totalUnread = notificationUnread + changelogUnread;

  const openChangelog = () => {
    setMenuOpen(false);
    setChangelogOpen(true);
    // Optimistically zero out the badge — panel will hard-refresh its own
    // state from the API, but the user has clearly seen the entry list.
    setChangelogUnread(0);
  };

  const goNotifications = () => {
    setMenuOpen(false);
    navigate('/notifications');
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="header-action-btn ha-mobile-only header-overflow-trigger"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={totalUnread > 0 ? `Updates and notifications (${totalUnread} unread)` : 'Updates and notifications'}
        aria-expanded={menuOpen}
      >
        <BellRing size={18} aria-hidden="true" />
        {totalUnread > 0 && (
          <Badge variant="error" size="sm" className="header-overflow-badge">
            {totalUnread > 99 ? '99+' : totalUnread}
          </Badge>
        )}
      </button>

      {menuOpen && (
        <div ref={menuRef} className="header-overflow-menu" role="menu">
          <button type="button" role="menuitem" className="header-overflow-item" onClick={openChangelog}>
            <span className="header-overflow-item-icon"><Sparkles size={16} /></span>
            <span className="header-overflow-item-label">What's New</span>
            {changelogUnread > 0 && (
              <Badge variant="error" size="sm" className="header-overflow-item-badge">
                {changelogUnread > 99 ? '99+' : changelogUnread}
              </Badge>
            )}
          </button>

          <button type="button" role="menuitem" className="header-overflow-item" onClick={goNotifications}>
            <span className="header-overflow-item-icon"><Bell size={16} /></span>
            <span className="header-overflow-item-label">Notifications</span>
            {notificationUnread > 0 && (
              <Badge variant="error" size="sm" className="header-overflow-item-badge">
                {notificationUnread > 99 ? '99+' : notificationUnread}
              </Badge>
            )}
          </button>
        </div>
      )}

      {changelogOpen && <ChangelogPanel panelRef={panelRef} onClose={() => setChangelogOpen(false)} />}
    </>
  );
};

export default HeaderOverflowMenu;
